import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const LIFF_ID = process.env.REACT_APP_LINE_LIFF_ID;

// 外圈弧：r=54, 周長=339 → 3/4弧=254, gap=85
const OUTER_DASH = '254 85';
// 内圈弧：r=44, 周長=276 → 1/2弧=138, gap=138
const INNER_DASH = '138 138';

export default function LiffEntry() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { login } = useAuth();

  useEffect(() => {
    // redirectTo パラメータを解析（直接 or liff.state 経由）
    const params = new URLSearchParams(location.search);
    let redirectTo: string | null = params.get('redirectTo');
    if (!redirectTo) {
      const liffState = params.get('liff.state');
      if (liffState) {
        const sp = new URLSearchParams(
          liffState.startsWith('?') ? liffState.slice(1) : liffState
        );
        redirectTo = sp.get('redirectTo');
      }
    }
    const target = redirectTo || '/mypage';

    const init = async () => {
      if (!LIFF_ID) {
        navigate(target, { replace: true });
        return;
      }
      try {
        const liff = (await import('@line/liff')).default;
        await liff.init({ liffId: LIFF_ID });

        // LINE外ブラウザからアクセスした場合はLPへ
        if (!liff.isInClient()) {
          navigate('/', { replace: true });
          return;
        }

        // 未ログインならLINE認証を促す
        if (!liff.isLoggedIn()) {
          liff.login({ redirectUri: window.location.href });
          return;
        }

        // バックエンドJWT保持済みなら直接遷移
        if (localStorage.getItem('token')) {
          navigate(target, { replace: true });
          return;
        }

        // idTokenでバックエンドログイン
        const idToken = liff.getIDToken();
        if (!idToken) throw new Error('No ID Token');

        const res = await api.post('/auth/line', { id_token: idToken });
        login(res.data.access_token, res.data.role, 'line');
        navigate(res.data.is_new_user ? '/welcome' : target, { replace: true });

      } catch (err) {
        console.error('LIFF init failed', err);
        navigate('/', { replace: true });
      }
    };

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <style>{`
        @keyframes liff-spin-cw {
          to { transform: rotate(360deg); }
        }
        @keyframes liff-spin-ccw {
          to { transform: rotate(-360deg); }
        }
        @keyframes liff-heartbeat {
          0%, 100% { transform: scale(1);    opacity: 1;    }
          15%       { transform: scale(1.13); opacity: 1;    }
          30%       { transform: scale(0.97); opacity: 0.95; }
          45%       { transform: scale(1.08); opacity: 1;    }
          65%       { transform: scale(1);    opacity: 1;    }
        }
        @keyframes liff-fade-text {
          0%, 100% { opacity: 0.35; }
          50%       { opacity: 1;    }
        }
        @keyframes liff-card-in {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes liff-dot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
          40%           { transform: scale(1);   opacity: 1;   }
        }
      `}</style>

      {/* 背景グラデーション */}
      <div style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(150deg, hsl(12 76% 61% / 0.10) 0%, hsl(20 80% 55% / 0.05) 40%, #fff9f8 100%)',
      }}>

        {/* 磨砂ガラスカード */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '28px',
          padding: '52px 44px 44px',
          background: 'rgba(255, 255, 255, 0.82)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: '36px',
          boxShadow: [
            '0 24px 64px rgba(232, 105, 74, 0.13)',
            '0 6px 24px rgba(0,0,0,0.07)',
            'inset 0 1px 0 rgba(255,255,255,0.9)',
          ].join(', '),
          border: '1px solid rgba(232, 105, 74, 0.14)',
          animation: 'liff-card-in 0.6s cubic-bezier(0.16,1,0.3,1) forwards',
        }}>

          {/* スピナー + ロゴ */}
          <div style={{ position: 'relative', width: '128px', height: '128px' }}>

            {/* 外弧（コーラル・時計回り） */}
            <svg
              width="128" height="128" viewBox="0 0 128 128"
              style={{
                position: 'absolute', top: 0, left: 0,
                animation: 'liff-spin-cw 1.8s linear infinite',
                transformOrigin: '64px 64px',
              }}
            >
              <circle
                cx="64" cy="64" r="60"
                fill="none"
                stroke="hsl(12 76% 61%)"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeDasharray="283 94"
              />
            </svg>

            {/* 内弧（薄コーラル・反時計回り） */}
            <svg
              width="104" height="104" viewBox="0 0 104 104"
              style={{
                position: 'absolute', top: '12px', left: '12px',
                animation: 'liff-spin-ccw 2.6s linear infinite',
                transformOrigin: '52px 52px',
              }}
            >
              <circle
                cx="52" cy="52" r="47"
                fill="none"
                stroke="hsl(12 76% 75%)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="148 148"
              />
            </svg>

            {/* 最内弧（極薄・時計回り遅め） */}
            <svg
              width="82" height="82" viewBox="0 0 82 82"
              style={{
                position: 'absolute', top: '23px', left: '23px',
                animation: 'liff-spin-cw 3.8s linear infinite',
                transformOrigin: '41px 41px',
              }}
            >
              <circle
                cx="41" cy="41" r="36"
                fill="none"
                stroke="hsl(12 76% 88%)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeDasharray="75 151"
              />
            </svg>

            {/* ロゴ（位置決め wrapper + 心拍 wrapper を分離して transform 競合を回避） */}
            <div style={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div style={{ animation: 'liff-heartbeat 2s ease-in-out infinite' }}>
                <img
                  src="/logo192.png"
                  alt="Melodis"
                  style={{
                    width: '66px',
                    height: '66px',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 2px 10px rgba(232,105,74,0.30))',
                  }}
                />
              </div>
            </div>
          </div>

          {/* クリニック名 + ローディングテキスト */}
          <div style={{ textAlign: 'center', userSelect: 'none' }}>
            <div style={{
              fontSize: '17px',
              fontWeight: '600',
              color: '#28334a',
              letterSpacing: '0.06em',
              marginBottom: '10px',
            }}>
              Melodis Clinic
            </div>

            {/* ドットアニメーション */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '5px',
            }}>
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{
                    display: 'inline-block',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: 'hsl(12 76% 61%)',
                    animation: `liff-dot 1.4s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
